import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const HEADER_STYLE = {
  padding: '22px 28px 18px',
  borderBottom: '1px solid #E2E8F0',
  background: '#fff',
};

const CARD_STYLE = {
  background: '#fff',
  border: '1px solid #E2E8F0',
  borderRadius: '14px',
  overflow: 'hidden',
};

const BTN_PRIMARY = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  padding: '10px 20px',
  border: 'none',
  borderRadius: '10px',
  background: '#122A5C',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 700,
  fontFamily: 'Manrope, sans-serif',
  cursor: 'pointer',
  letterSpacing: '-0.01em',
};

const BTN_SECONDARY = {
  ...BTN_PRIMARY,
  background: '#F1F5F9',
  color: '#122A5C',
  border: '1px solid #D7DEE9',
};

const CHECKBOX_STYLE = {
  width: '18px',
  height: '18px',
  accentColor: '#E07000',
  cursor: 'pointer',
};

const SELECT_STYLE = {
  padding: '6px 10px',
  border: '1px solid #D7DEE9',
  borderRadius: '8px',
  fontSize: '12px',
  fontFamily: 'Manrope, sans-serif',
  color: '#122A5C',
  background: '#fff',
  minWidth: '160px',
  maxWidth: '220px',
  cursor: 'pointer',
};

function buildDepartmentTree(departments) {
  const map = {};
  for (const d of departments) {
    map[d.ID] = { ...d, children: [] };
  }
  const roots = [];
  for (const d of departments) {
    const node = map[d.ID];
    if (d.PARENT && map[d.PARENT]) {
      map[d.PARENT].children.push(node);
    } else {
      roots.push(node);
    }
  }

  const flat = [];
  const walk = (nodes, depth = 0, parentPath = '') => {
    for (const n of nodes) {
      const path = parentPath ? `${parentPath} > ${n.NAME}` : n.NAME;
      const prefix = depth > 0 ? '—'.repeat(depth) + ' ' : '';
      flat.push({ id: n.ID, name: n.NAME, depth, prefix, path });
      if (n.children.length > 0) walk(n.children, depth + 1, path);
    }
  };
  walk(roots);
  return flat;
}

function DepartmentSelect({ value, onChange, departmentTree }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={SELECT_STYLE}>
      <option value="">Bitte wählen…</option>
      {departmentTree.map((d) => (
        <option key={d.id} value={d.id} title={d.path}>
          {d.prefix}{d.name}
        </option>
      ))}
    </select>
  );
}

export default function BitrixUserCreator({ apiBase = '/api', session = null }) {
  const accessToken = session?.access_token || null;
  const isAuthenticated = Boolean(accessToken);

  const [deals, setDeals] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dealSettings, setDealSettings] = useState({});
  const [creating, setCreating] = useState(false);
  const [results, setResults] = useState(null);
  const [phaseFilter, setPhaseFilter] = useState('bitrix');
  const [stages, setStages] = useState([]);
  const [moveDeals, setMoveDeals] = useState(false);
  const [moveToStage, setMoveToStage] = useState('');

  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  const loadData = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Bitte zuerst einloggen.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [dealsRes, deptsRes, stagesRes] = await Promise.all([
        axios.get(`${apiBase}/contracts/usage`, {
          params: { op: 'vermittler-bitrix-deals', phases: phaseFilter, _ts: Date.now() },
          headers: authHeaders,
        }),
        axios.get(`${apiBase}/contracts/usage`, {
          params: { op: 'bitrix-departments', _ts: Date.now() },
          headers: authHeaders,
        }),
        axios.get(`${apiBase}/contracts/usage`, {
          params: { op: 'vermittler-stages', _ts: Date.now() },
          headers: authHeaders,
        }),
      ]);
      const items = Array.isArray(dealsRes.data?.items) ? dealsRes.data.items : [];
      const depts = Array.isArray(deptsRes.data?.departments) ? deptsRes.data.departments : [];
      const stgs = Array.isArray(stagesRes.data?.stages) ? stagesRes.data.stages : [];
      setDeals(items);
      setDepartments(depts);
      setStages(stgs);
      setSelectedIds(new Set());
      setDealSettings({});
      setResults(null);
      if (items.length === 0) {
        toast.info('Keine Deals gefunden.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Daten konnten nicht geladen werden.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders, isAuthenticated, phaseFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const departmentTree = useMemo(() => buildDepartmentTree(departments), [departments]);

  const updateDealSetting = useCallback((dealId, key, value) => {
    setDealSettings((prev) => ({
      ...prev,
      [dealId]: { ...prev[dealId], [key]: value },
    }));
  }, []);

  const setAllDepartments = useCallback((deptId) => {
    setDealSettings((prev) => {
      const next = { ...prev };
      for (const deal of deals) {
        next[deal.dealId] = { ...next[deal.dealId], departmentId: deptId };
      }
      return next;
    });
  }, [deals]);

  const toggleSelect = useCallback((dealId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (selectedIds.size === deals.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(deals.map((d) => d.dealId)));
  }, [deals, selectedIds.size]);

  const selectedDealsValid = useMemo(() => {
    if (selectedIds.size === 0) return false;
    for (const id of selectedIds) {
      if (!dealSettings[id]?.departmentId) return false;
    }
    return true;
  }, [selectedIds, dealSettings]);

  const handleCreate = useCallback(async () => {
    if (selectedIds.size === 0) {
      toast.warning('Bitte mindestens einen Deal auswählen.');
      return;
    }
    const missing = [];
    for (const id of selectedIds) {
      if (!dealSettings[id]?.departmentId) {
        const deal = deals.find((d) => d.dealId === id);
        missing.push(deal?.contactName || `Deal ${id}`);
      }
    }
    if (missing.length > 0) {
      toast.warning(`Abteilung fehlt bei: ${missing.join(', ')}`);
      return;
    }
    setCreating(true);
    setResults(null);
    try {
      const users = deals
        .filter((d) => selectedIds.has(d.dealId))
        .map((d) => ({
          dealId: d.dealId,
          email: d.email,
          firstName: d.firstName,
          lastName: d.lastName,
          departmentId: dealSettings[d.dealId]?.departmentId,
          isHead: dealSettings[d.dealId]?.isHead || false,
        }));
      const payload = {
        action: 'vermittler-bitrix-create-users',
        users,
      };
      if (moveDeals && moveToStage) {
        payload.moveToStage = moveToStage;
      }
      const response = await axios.post(`${apiBase}/contracts/usage`, payload, { headers: authHeaders });
      setResults(response.data?.results || []);
      const ok = (response.data?.results || []).filter((r) => r.ok).length;
      const failed = (response.data?.results || []).filter((r) => !r.ok).length;
      if (ok > 0) toast.success(`${ok} Bitrix-Nutzer erstellt.`);
      if (failed > 0) toast.error(`${failed} fehlgeschlagen — Details unten.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Nutzer-Erstellung fehlgeschlagen.');
    } finally {
      setCreating(false);
    }
  }, [apiBase, authHeaders, deals, dealSettings, selectedIds, moveDeals, moveToStage]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC', fontFamily: 'Manrope, sans-serif' }}>
      <div style={HEADER_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', fontWeight: 700, color: '#E07000', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
              Vermittler On-Boarding
            </div>
            <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 700, color: '#122A5C', letterSpacing: '-0.03em' }}>
              Bitrix Zugänge erstellen
            </h2>
            <div style={{ color: '#64748B', fontSize: '13px', marginTop: '4px' }}>
              Bitrix24-Benutzerkonten für neue Vertriebler anlegen. Abteilung und Leitungsfunktion pro Nutzer wählbar.
            </div>
          </div>
          <button onClick={loadData} disabled={loading} style={BTN_SECONDARY}>
            {loading ? 'Lade…' : 'Aktualisieren'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '0', marginTop: '14px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #D7DEE9', width: 'fit-content' }}>
          {[
            { value: 'bitrix', label: 'Bitrix Zugang erstellen' },
            { value: 'it', label: 'IT einrichten' },
            { value: 'both', label: 'Beide Phasen' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPhaseFilter(opt.value)}
              style={{
                padding: '7px 16px',
                border: 'none',
                borderRight: '1px solid #D7DEE9',
                background: phaseFilter === opt.value ? '#122A5C' : '#fff',
                color: phaseFilter === opt.value ? '#fff' : '#475569',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: 'Manrope, sans-serif',
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '20px 28px 40px', maxWidth: '1200px' }}>
        {error && (
          <div style={{ padding: '14px 18px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', color: '#991B1B', fontSize: '13px', marginBottom: '18px' }}>
            {error}
          </div>
        )}

        {deals.length > 0 && (
          <>
            {/* Bulk department assignment */}
            <div style={{ ...CARD_STYLE, marginBottom: '12px' }}>
              <div style={{ padding: '12px 18px', background: '#F8FAFC', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Allen dieselbe Abteilung zuweisen:</span>
                <DepartmentSelect value="" onChange={setAllDepartments} departmentTree={departmentTree} />
              </div>
            </div>

            {/* Phase move option */}
            <div style={{ ...CARD_STYLE, marginBottom: '12px' }}>
              <div style={{ padding: '12px 18px', background: '#F8FAFC', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={moveDeals}
                    onChange={(e) => { setMoveDeals(e.target.checked); if (!e.target.checked) setMoveToStage(''); }}
                    style={{ ...CHECKBOX_STYLE, width: '16px', height: '16px' }}
                  />
                  Deals nach Erstellung in andere Phase verschieben
                </label>
                {moveDeals && (
                  <select
                    value={moveToStage}
                    onChange={(e) => setMoveToStage(e.target.value)}
                    style={SELECT_STYLE}
                  >
                    <option value="">Ziel-Phase wählen…</option>
                    {stages.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Deal list */}
            <div style={{ ...CARD_STYLE, marginBottom: '20px' }}>
              <div style={{ padding: '14px 18px', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#122A5C' }}>
                  Deals ({deals.length})
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button onClick={toggleAll} style={BTN_SECONDARY}>
                    {selectedIds.size === deals.length ? 'Keine auswählen' : 'Alle auswählen'}
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={!selectedDealsValid || creating}
                    style={{ ...BTN_PRIMARY, background: '#E07000', opacity: (!selectedDealsValid || creating) ? 0.5 : 1 }}
                  >
                    {creating ? 'Erstelle…' : `Nutzer erstellen (${selectedIds.size})`}
                  </button>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#F8FAFC' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0', width: '40px' }}> </th>
                      <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Deal</th>
                      {phaseFilter === 'both' && <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Phase</th>}
                      <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Kontakt</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>E-Mail</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Abteilung</th>
                      <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Leiter</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deals.map((deal) => {
                      const isSelected = selectedIds.has(deal.dealId);
                      const result = results?.find((r) => r.dealId === deal.dealId);
                      const settings = dealSettings[deal.dealId] || {};
                      return (
                        <tr
                          key={deal.dealId}
                          style={{ background: isSelected ? '#FFF7ED' : '#fff', transition: 'background 0.15s' }}
                        >
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9', cursor: 'pointer' }} onClick={() => toggleSelect(deal.dealId)}>
                            <input type="checkbox" checked={isSelected} onChange={() => {}} style={CHECKBOX_STYLE} />
                          </td>
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9', color: '#122A5C', fontWeight: 600, cursor: 'pointer' }} onClick={() => toggleSelect(deal.dealId)}>
                            <div>{deal.dealTitle}</div>
                            <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 400 }}>ID {deal.dealId}</div>
                          </td>
                          {phaseFilter === 'both' && (
                            <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
                              <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: deal.phase === 'IT einrichten' ? '#DBEAFE' : '#E0E7FF', color: deal.phase === 'IT einrichten' ? '#1E40AF' : '#3730A3' }}>
                                {deal.phase}
                              </span>
                            </td>
                          )}
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9', color: '#334155', cursor: 'pointer' }} onClick={() => toggleSelect(deal.dealId)}>
                            {deal.contactName || 'n/a'}
                          </td>
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
                            {deal.email ? (
                              <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#059669', fontWeight: 600 }}>{deal.email}</span>
                            ) : (
                              <span style={{ color: '#EF4444', fontSize: '12px', fontWeight: 600 }}>Keine E-Mail</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
                            <DepartmentSelect
                              value={settings.departmentId || ''}
                              onChange={(v) => updateDealSetting(deal.dealId, 'departmentId', v)}
                              departmentTree={departmentTree}
                            />
                          </td>
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={settings.isHead || false}
                              onChange={(e) => updateDealSetting(deal.dealId, 'isHead', e.target.checked)}
                              style={{ ...CHECKBOX_STYLE, accentColor: '#7C3AED' }}
                            />
                          </td>
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
                            {result ? (
                              result.ok ? (
                                <span style={{ color: '#059669', fontWeight: 600, fontSize: '12px' }}>
                                  Erstellt (ID {result.userId}){result.headSet && ' — Leiter'}{result.moved && ' — verschoben'}
                                </span>
                              ) : (
                                <span style={{ color: '#EF4444', fontWeight: 600, fontSize: '12px' }}>{result.error || 'Fehler'}</span>
                              )
                            ) : (
                              <span style={{ color: '#CBD5E1' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {results && results.length > 0 && (
          <div style={{ ...CARD_STYLE }}>
            <div style={{
              padding: '14px 18px',
              background: results.every((r) => r.ok) ? '#F0FDF4' : '#FFFBEB',
              borderBottom: '1px solid ' + (results.every((r) => r.ok) ? '#BBF7D0' : '#FDE68A'),
            }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: results.every((r) => r.ok) ? '#166534' : '#92400E' }}>
                Ergebnis: {results.filter((r) => r.ok).length} erstellt, {results.filter((r) => !r.ok).length} fehlgeschlagen
              </div>
            </div>
            <div style={{ padding: '14px 18px' }}>
              {results.map((r, i) => (
                <div key={i} style={{ padding: '6px 0', fontSize: '13px', borderBottom: i < results.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                  <span style={{ fontWeight: 600, color: '#122A5C' }}>Deal {r.dealId}</span>
                  {' — '}
                  {r.ok ? (
                    <span style={{ color: '#059669' }}>Nutzer erstellt (Bitrix-User-ID: {r.userId}){r.headSet && ' — als Leiter eingesetzt'}{r.moved && ' — Deal verschoben'}{r.moveError && ` — Verschiebung fehlgeschlagen: ${r.moveError}`}</span>
                  ) : (
                    <span style={{ color: '#EF4444' }}>{r.error}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && deals.length === 0 && !error && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94A3B8', fontSize: '14px' }}>
            Keine Deals in der Vermittler-Pipeline gefunden.
          </div>
        )}
      </div>
    </div>
  );
}
